import { defineStore } from 'pinia';
import { nanoid } from 'nanoid';

const CLICK_EVENT = 'click';
const ADD_TO_CART_EVENT = 'addtocart';
const PURCHASE_EVENT = 'purchase';

type TrackingEvent = typeof CLICK_EVENT | typeof ADD_TO_CART_EVENT | typeof PURCHASE_EVENT;

type Loop54TrackingItem = {
  id?: string; // articleNumber
  quantity?: number;
};

type Loop54Event = {
  type: TrackingEvent;
  entity: {
    type: string;
    id?: string;
  };
  quantity?: number;
  orderId?: string;
};

export const useTrackingStore = defineStore('tracking', () => {
  const trackingId = ref<string>('');
  const cacheKey = ref<string>('_l54a');

  const generateLoop54Events = (
    type: TrackingEvent,
    items: Loop54TrackingItem[],
    orderId = '',
  ): { events: Loop54Event[] } => {
    const result: { events: Loop54Event[] } = { events: [] };
    for (const item of items) {
      const event: Loop54Event = {
        type,
        entity: {
          type: 'Variant',
          id: item.id,
        },
      };

      if (type === ADD_TO_CART_EVENT) {
        event.quantity = item.quantity;
      } else if (type === PURCHASE_EVENT) {
        event.quantity = item.quantity;
        event.orderId = orderId;
      }

      result.events.push(event);
    }

    return result;
  };

  const initiateTrackingId = (): void => {
    let id = localStorage.getItem(cacheKey.value);
    if (!id) {
      id = nanoid();
      localStorage.setItem(cacheKey.value, id);
    }
    trackingId.value = id;
  };

  const fireLoop54Events = async (payload: { events: Loop54Event[] }): Promise<void | string> => {
    const { $searchService, $sentry } = useNuxtApp();
    if (!trackingId.value || trackingId.value === '') return $sentry.captureException('No trackingId available.');
    const { error } = await $searchService.trackBehaviour(payload, trackingId.value);
    if (error) $sentry.captureException(error);
  };

  const visitProduct = (articleNumber?: string): void => {
    if (articleNumber) {
      const data = generateLoop54Events(CLICK_EVENT, [{ id: articleNumber }]);
      fireLoop54Events(data);
    }
  };

  const addToCart = ({ articleNumber, quantity }: { articleNumber: string; quantity: number }): void => {
    if (articleNumber && quantity) {
      const data = generateLoop54Events(ADD_TO_CART_EVENT, [{ id: articleNumber, quantity }]);
      fireLoop54Events(data);
    }
  };

  const purchase = ({
    orderId,
    orderItems,
  }: {
    orderId: string;
    orderItems: { articleNumber: string; quantity: number }[];
  }): void => {
    const items = orderItems
      .filter((item) => item.articleNumber && item.quantity)
      .map<Loop54TrackingItem>((item) => ({
        id: item.articleNumber,
        quantity: item.quantity,
      }));

    if (orderId && items.length) {
      const data = generateLoop54Events(PURCHASE_EVENT, items, orderId);
      fireLoop54Events(data);
    }
  };

  return {
    cacheKey,
    addToCart,
    visitProduct,
    purchase,
    trackingId,
    initiateTrackingId,
    // ToDo: Exported just for tests. Can we do this any other way?
    fireLoop54Events,
  };
});
